import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { postService } from '../../services/postService';
import { categoryService } from '../../services/categoryService';
import DataTable from '../components/common/DataTable';
import FormModal from '../components/common/FormModal';
import InfoModal from '../components/common/InfoModal';
import DeleteConfirmation from '../components/common/DeleteConfirmation';
import ToastNotification from '../components/common/ToastNotification';

function PostList() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  
  // Modal states
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await postService.getAdminArticle();
      setPosts(response.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAll();
      setCategories(response.data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const showNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const handleAdd = () => {
    navigate('/admin/posts/create');
  };

  const handleView = (post) => {
    setSelectedPost(post);
    setShowInfoModal(true);
  };

  const handleEdit = (post) => {
    navigate(`/admin/posts/edit/${post.id}`);
  };

  const handleContentBlocks = (post) => {
    navigate(`/admin/posts/${post.id}/content`);
  };

  const handleDeleteClick = (post) => {
    setSelectedPost(post);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await postService.delete(selectedPost.id);
      showNotification('Post deleted successfully');
      setShowDeleteModal(false);
      setSelectedPost(null);
      await fetchPosts();
    } catch (err) {
      showNotification(err.message, 'danger');
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  const columns = [
    { 
      key: 'id', 
      header: 'ID',
      render: (value) => <span style={{ fontWeight: '500' }}>#{value}</span>
    },
    { 
      key: 'title', 
      header: 'Title',
      render: (value, item) => (
        <div>
          <div style={{ fontWeight: '500' }}>{value}</div>
          <small className="admin-crud-empty-subtext">
            {item.description?.substring(0, 60)}...
          </small>
        </div>
      )
    },
    { 
      key: 'categoryId', 
      header: 'Category',
      render: (value) => getCategoryName(value)
    },
    { 
      key: 'isTutorials', 
      header: 'Type',
      render: (value) => (
        <span className={`admin-crud-badge ${value ? 'admin-crud-badge-success' : 'admin-crud-badge-info'}`}>
          {value ? '📘 Tutorial' : '📝 Blog'}
        </span>
      )
    },
    { 
      key: 'isPublished', 
      header: 'Status',
      render: (value) => (
        <span className={`admin-crud-badge ${value ? 'admin-crud-badge-success' : 'admin-crud-badge-warning'}`}>
          {value ? '✅ Published' : '📝 Draft'}
        </span>
      )
    },
    { 
      key: 'createdAt', 
      header: 'Date',
      render: (value) => new Date(value).toLocaleDateString()
    }
  ];

  const infoFields = [
    { key: 'id', label: 'Post ID' },
    { key: 'title', label: 'Title' },
    { key: 'description', label: 'Description' },
    { key: 'categoryId', label: 'Category', render: (value) => getCategoryName(value) },
    { 
      key: 'isTutorials', 
      label: 'Type',
      render: (value) => value ? 'Tutorial' : 'Blog',
      badge: (value) => value ? 'success' : 'info'
    },
    { 
      key: 'isPublished', 
      label: 'Status',
      render: (value) => value ? 'Published' : 'Draft',
      badge: (value) => value ? 'success' : 'warning'
    },
    { 
      key: 'displayOrder', 
      label: 'Display Order',
      render: (value) => value || '0'
    },
    { 
      key: 'coverImage', 
      label: 'Cover Image',
      render: (value) => value ? (
        <img src={value} alt="Cover" style={{ maxWidth: '200px', borderRadius: '8px' }} />
      ) : 'No image'
    },
    { 
      key: 'createdAt', 
      label: 'Created Date',
      render: (value) => new Date(value).toLocaleString()
    },
    { 
      key: 'updatedAt', 
      label: 'Last Updated',
      render: (value) => value ? new Date(value).toLocaleString() : 'N/A'
    }
  ];

  // Custom actions for posts
  const renderCustomActions = (post) => (
    <div className="admin-crud-actions-group">
      <button
        className="admin-crud-action-btn admin-crud-action-view"
        onClick={() => handleView(post)}
        title="View Details"
      >
        👁️ View
      </button>
      <button
        className="admin-crud-action-btn"
        style={{ color: '#8b5cf6', borderColor: '#8b5cf6' }}
        onClick={() => handleContentBlocks(post)}
        title="Manage Content Blocks"
      >
        📦 Blocks
      </button>
      <button
        className="admin-crud-action-btn admin-crud-action-edit"
        onClick={() => handleEdit(post)}
        title="Edit Post"
      >
        ✏️ Edit
      </button>
      <button
        className="admin-crud-action-btn admin-crud-action-delete"
        onClick={() => handleDeleteClick(post)}
        title="Delete Post"
      >
        🗑️ Delete
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="admin-crud-wrapper">
        <div className="admin-crud-loader">
          <div className="admin-crud-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-crud-wrapper">
      <div className="admin-crud-container">
        {error && (
          <div className="admin-crud-toast" style={{ position: 'relative', marginBottom: '20px' }}>
            <div className="admin-crud-toast-content admin-crud-toast-danger">
              <div className="admin-crud-toast-header">
                <strong>Error</strong>
                <button onClick={() => setError(null)}>✕</button>
              </div>
              <div className="admin-crud-toast-body">{error}</div>
            </div>
          </div>
        )}

        <DataTable
          title="Posts Management"
          columns={columns}
          data={posts}
          onAdd={handleAdd}
          renderActions={renderCustomActions}
          addButtonText="Create New Post"
          searchPlaceholder="Search posts by title or description..."
        />

        {/* Info/View Modal */}
        <InfoModal
          show={showInfoModal}
          onHide={() => {
            setShowInfoModal(false);
            setSelectedPost(null);
          }}
          title="Post Details"
          data={selectedPost}
          fields={infoFields}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmation
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedPost(null);
          }}
          onConfirm={handleConfirmDelete}
          title="Delete Post"
          message={`Are you sure you want to delete "${selectedPost?.title}"? This action cannot be undone.`}
        />

        {/* Toast Notification */}
        <ToastNotification
          show={showToast}
          onClose={() => setShowToast(false)}
          message={toastMessage}
          type={toastType}
        />
      </div>
    </div>
  );
}

export default PostList;